import { useState } from "react";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, Clock, MessageCircle, Send } from "lucide-react";
import Layout from "@/components/Layout";
import SEOHead from "@/components/SEOHead";

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5 },
};

const Contact = () => {
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const msg = `Name: ${form.name}\nPhone: ${form.phone}\nEmail: ${form.email}\nMessage: ${form.message}`;
    window.open(`https://wa.me/919952493907?text=${encodeURIComponent(msg)}`, "_blank");
  };

  return (
    <Layout>
      <SEOHead
        title="Contact Pondicherry Taxi — Call, WhatsApp & Cab Booking in Puducherry | 24/7 Available"
        description="Contact Pondicherry Taxi for airport pickup, outstation cab, one-way drop, and local sightseeing bookings. Call +91 99524 93907 or WhatsApp us — 24/7 taxi service in Puducherry."
        keywords="contact Pondicherry taxi, cab booking Pondicherry, taxi service near me Pondicherry, taxi near me Pondicherry, cab near me Puducherry, Pondicherry airport taxi service, airport pickup and drop Pondicherry, Chennai to Pondicherry taxi, Pondicherry to Chennai cab, one-way drop taxi Pondicherry, outstation taxi Pondicherry, 24/7 taxi Pondicherry, affordable taxi Pondicherry, Pondicherry local sightseeing taxi, corporate cab service Pondicherry, office pickup drop taxi Pondicherry"
        canonical="https://pondicherry.taxi/contact"
      />

      <section className="pt-28 pb-16 section-padding bg-gradient-to-b from-primary/5 to-transparent">
        <div className="container-custom text-center">
          <motion.h1 {...fadeUp} className="text-4xl sm:text-5xl font-bold">
            Contact <span className="text-gradient">Pondicherry Taxi</span>
          </motion.h1>
          <motion.p {...fadeUp} className="mt-4 text-muted-foreground max-w-2xl mx-auto">
            Ready to book a cab in Pondicherry? Call or WhatsApp us for airport taxi, one-way drop, outstation trips, and local sightseeing — available 24/7 across Puducherry.
          </motion.p>
        </div>
      </section>

      <section className="section-padding">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-10">
            {/* Contact Info */}
            <div className="space-y-6">
              <motion.div {...fadeUp} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  { icon: Phone, label: "Call Us", value: "+91 99524 93907", href: "tel:+919952493907" },
                  { icon: MessageCircle, label: "WhatsApp", value: "Chat with us", href: "https://wa.me/919952493907" },
                  { icon: Mail, label: "Email", value: "elitetravelspondy@gmail.com", href: "mailto:elitetravelspondy@gmail.com" },
                  { icon: Clock, label: "Hours", value: "24/7 Available", href: undefined },
                ].map((c) => (
                  <div key={c.label} className="glass-card-hover rounded-2xl p-5">
                    <c.icon className="w-6 h-6 text-primary mb-3" />
                    <p className="text-xs text-muted-foreground">{c.label}</p>
                    {c.label === "Call Us" ? (
                      <div className="flex flex-col gap-1">
                        <a href="tel:+919952493907" className="text-sm font-semibold text-foreground hover:text-primary transition-colors">
                          +91 99524 93907
                        </a>
                        <a href="tel:+919500454165" className="text-[13px] font-semibold text-muted-foreground hover:text-primary transition-colors">
                          +91 95004 54165
                        </a>
                      </div>
                    ) : c.href ? (
                      <a href={c.href} target={c.href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer" className="text-sm font-semibold text-foreground hover:text-primary transition-colors">
                        {c.value}
                      </a>
                    ) : (
                      <p className="text-sm font-semibold text-foreground">{c.value}</p>
                    )}
                  </div>
                ))}
              </motion.div>

              <motion.div {...fadeUp} className="glass-card rounded-2xl p-5">
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-foreground">Our Office</p>
                    <p className="text-sm text-muted-foreground">35, 2 nd Cross, Sivagami Nagar, Reddiyarpalayam, Pondicherry, 605010.</p>
                  </div>
                </div>
              </motion.div>

              {/* Map */}
              <motion.div {...fadeUp} className="glass-card rounded-2xl overflow-hidden h-[300px]">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d31109.88547987043!2d79.7883!3d11.9344!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3a5361ab8e49cfcf%3A0xcc6bd326d2f0b04e!2sPuducherry!5e0!3m2!1sen!2sin!4v1"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Pondicherry Taxi — Cab Service Office Location, Puducherry"
                />
              </motion.div>
            </div>

            {/* Form */}
            <motion.form
              {...fadeUp}
              onSubmit={handleSubmit}
              className="glass-card rounded-2xl p-6 sm:p-8 h-fit"
            >
              <h2 className="text-xl font-bold text-foreground mb-6">Book a Taxi in Pondicherry — Send Us a Message</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-muted-foreground mb-1.5">Your Name</label>
                  <input
                    type="text"
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full bg-secondary/50 border border-border rounded-lg py-3 px-4 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary transition-colors"
                    placeholder="Enter your name"
                  />
                </div>
                <div>
                  <label className="block text-sm text-muted-foreground mb-1.5">Phone Number</label>
                  <input
                    type="tel"
                    required
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="w-full bg-secondary/50 border border-border rounded-lg py-3 px-4 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary transition-colors"
                    placeholder="+91 99524 93907"
                  />
                </div>
                <div>
                  <label className="block text-sm text-muted-foreground mb-1.5">Email (Optional)</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="w-full bg-secondary/50 border border-border rounded-lg py-3 px-4 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary transition-colors"
                    placeholder="your@email.com"
                  />
                </div>
                <div>
                  <label className="block text-sm text-muted-foreground mb-1.5">Message</label>
                  <textarea
                    required
                    rows={4}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    className="w-full bg-secondary/50 border border-border rounded-lg py-3 px-4 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary transition-colors resize-none"
                    placeholder="E.g. Airport pickup, Chennai to Pondicherry taxi, local sightseeing, outstation trip..."
                  />
                </div>
                <button type="submit" className="btn-primary w-full flex items-center justify-center gap-2">
                  <Send className="w-4 h-4" /> Send Message
                </button>
              </div>
            </motion.form>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Contact;
